import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Key as KeyIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";


const CreateUsers = () => {
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    mobile: "",
    email: "",
    username: "",
    password: "",
    userTypes: [], // Multi-select user types
    isMaskingEnabled: false,
    labTypes: [],
    status: 'Active',
    capacity: "",
    isQma: 0,
    isAcm: 0,
    isaplicable: 0,
  });
  
  const [showAssessorFields, setShowAssessorFields] = useState(false);
  const [showCabMasking, setShowCabMasking] = useState(false);
  const [requiresLabType, setRequiresLabType] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loginTypes, setLoginTypes] = useState([]);
  const [labTypes] = useState([
    { id: 1, name: "Testing" },
    { id: 2, name: "Calibration" },
    { id: 3, name: "Medical" },
    { id: 4, name: "PTP" },
    { id: 5, name: "RMP" }
  ]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [keywordSearch, setKeywordSearch] = useState("");
  const [filterUserType, setFilterUserType] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Initialize from localStorage and fetch data
  useEffect(() => {
    const storedLabTypeId = localStorage.getItem('labTypeId');
    if (storedLabTypeId) {
      const labTypeId = Number(storedLabTypeId);
      const labTypeName = labTypes.find(type => type.id === labTypeId)?.name || 'Unknown';
      console.log(`Lab Type ID: ${labTypeId}, Lab Type: ${labTypeName}`);
      
      // Set default lab type in form if needed
      if (labTypeId) {
        setFormData(prev => ({
          ...prev,
          labTypes: [labTypeId]
        }));
      }
    }
    
    fetchLoginTypes();
  }, []);
  
  // Fetch users when pagination, search, or filter changes (with debounce for search)
  useEffect(() => {
    if (loginTypes.length > 0) {
      const debounceTimer = setTimeout(() => {
        fetchUsers();
      }, keywordSearch ? 500 : 0); // 500ms debounce for search, immediate for others
      
      return () => clearTimeout(debounceTimer);
    }
  }, [page, rowsPerPage, keywordSearch, filterUserType, loginTypes]);

  // MODE 3 - Fetch Login Types
  const fetchLoginTypes = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/super-admin-master/login-types`
      );
      if (res.data.status === "success") {
        setLoginTypes(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching login types:", err);
    }
  };

  // MODE 5 - Fetch Users List (with pagination)
  const fetchUsers = async () => {
    setLoading(true);
    setError("");
    try {
      // Build SQL condition based on search and filter
      let condition = "";
      
      if (keywordSearch) {
        condition += ` AND (ut.Name LIKE '%${keywordSearch}%' OR ut.Email LIKE '%${keywordSearch}%' OR l.LoginName LIKE '%${keywordSearch}%' OR ut.Mobile LIKE '%${keywordSearch}%')`;
      }
      
      if (filterUserType) {
        const selectedType = loginTypes.find(t => t.LoginType === filterUserType);
        if (selectedType) {
          condition += ` AND l.UserTypeId = ${selectedType.Id}`;
        }
      }
      
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/super-admin-master/users`,
        { 
          cond: condition,
          page: page,
          pageSize: rowsPerPage
        }
      );
      if (res.data.status === "success") {
        setUsers(res.data.data || []);
        setTotalCount(res.data.totalCount || 0);
      } else {
        setError(res.data.message || "Failed to fetch users");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching users");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLabTypeChange = (labTypeId) => {
    const currentLabTypes = [...formData.labTypes];
    const index = currentLabTypes.indexOf(labTypeId);
    
    if (index === -1) {
      currentLabTypes.push(labTypeId);
    } else {
      currentLabTypes.splice(index, 1);
    }
    
    setFormData({ ...formData, labTypes: currentLabTypes });
  };

  // Handle user type selection changes to show/hide conditional fields (multi-select)
  const handleUserTypeChange = (event) => {
    const selectedTypes = event.target.value;
    setFormData({ ...formData, userTypes: selectedTypes });
    
    // Check if ANY selected type requires special fields
    // Assessor = 6, DO = 4, Director = 8, JD = 9
    const hasAssessor = selectedTypes.includes(6);
    const hasDO = selectedTypes.includes(4);
    const hasTypesRequiringLab = selectedTypes.some(typeId => [4, 6, 8, 9].includes(typeId));
    
    setShowAssessorFields(hasAssessor);
    setShowCabMasking(hasDO);
    setRequiresLabType(hasTypesRequiringLab);
  };

  const validateForm = () => {
    if (!formData.name) {
      setError("Name is required");
      return false;
    }
    if (!formData.mobile || formData.mobile.length < 10) {
      setError("Valid mobile number is required (10 digits)");
      return false;
    }
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setError("Valid email is required");
      return false;
    }
    if (!formData.username) {
      setError("Username/Assessor ID is required");
      return false;
    }
    if (!formData.id && !formData.password) {
      setError("Password is required for new users");
      return false;
    }
    if (formData.password && !validatePassword(formData.password)) {
      setError("Password must be alphanumeric with minimum 8 characters, at least 1 special character, 1 uppercase, and 1 lowercase letter");
      return false;
    }
    if (!formData.userTypes || formData.userTypes.length === 0) {
      setError("Select at least one Type of User.");
      return false;
    }
    
    // Check if lab type is required for selected user type
    if (requiresLabType && formData.labTypes.length === 0) {
      setError("Select Lab Type.");
      return false;
    }
    
    // Validate assessor-specific fields
    if (showAssessorFields) {
      if (!formData.capacity) {
        setError("Capacity is required for Assessor");
        return false;
      }
    }
    
    return true;
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return password.length >= minLength && 
           hasUpperCase && 
           hasLowerCase && 
           hasNumbers && 
           hasSpecialChar;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setError("");
    setLoading(true);
    
    try {
      const createdById = localStorage.getItem('orgId') || 1;
      const labTypeIds = formData.labTypes.join(',');
      
      if (formData.id) {
        // MODE 6 - Update User
        const updateData = {
          orgId: formData.id,
          name: formData.name,
          mobile: formData.mobile,
          email: formData.email,
          username: formData.username,
          labTypeId: formData.labTypes[0] || null,
          defaultLoginTypeId: formData.userTypes[0] || null, // Primary user type
          userTypeIds: formData.userTypes.join(','), // All selected user types
          updatedById: createdById,
          labTypeIds: labTypeIds,
          status: formData.status,
          capacity: showAssessorFields ? formData.capacity : null,
          isQma: showAssessorFields ? formData.isQma : null,
          isAcm: showAssessorFields ? formData.isAcm : null,
          isCabMasking: showCabMasking && formData.isMaskingEnabled ? 1 : 0,
          isaplicable: formData.isaplicable
        };
        
        await axios.put(
          `${process.env.REACT_APP_API_URL}/super-admin-master/update-user`,
          updateData
        );
        setSuccessMessage("User updated successfully!");
      } else {
        // MODE 1 - Create User
        const createData = {
          name: formData.name,
          mobile: formData.mobile,
          email: formData.email,
          username: formData.username,
          password: formData.password, // Send plain password - backend will hash it
          createdById: createdById,
          defaultLoginTypeId: formData.userTypes[0] || null, // Primary user type
          userTypeIds: formData.userTypes.join(','), // All selected user types
          labTypeId: formData.labTypes[0] || null,
          isLab: false, // Set to false for admin users
          labTypeIds: labTypeIds,
          status: formData.status,
          capacity: showAssessorFields ? formData.capacity : null,
          isQma: showAssessorFields ? formData.isQma : null,
          isAcm: showAssessorFields ? formData.isAcm : null,
          isCabMasking: showCabMasking && formData.isMaskingEnabled ? 1 : 0,
          isaplicable: formData.isaplicable
        };
        
        await axios.post(
          `${process.env.REACT_APP_API_URL}/super-admin-master/create-user`,
          createData
        );
        setSuccessMessage("User created successfully!");
      }
      
      fetchUsers();
      setTimeout(() => setSuccessMessage(""), 3000);
      handleReset();
    } catch (err) {
      setError(err.response?.data?.message || "Error saving user");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    // Parse LabTypeIds if it's a string
    let labTypesArray = [];
    if (user.LabTypeIds) {
      labTypesArray = user.LabTypeIds.split(',').map(id => parseInt(id)).filter(id => !isNaN(id));
    } else if (user.LabTypeId) {
      labTypesArray = [parseInt(user.LabTypeId)];
    }
    
    // Parse user types - handle both single UserTypeId and comma-separated UserRoles
    let userTypesArray = [];
    
    if (user.UserRoles) {
      // UserRoles is comma-separated role names like "SuperAdmin,Dealing Officer,Accounts"
      const roleNames = user.UserRoles.split(',').map(name => name.trim());
      // Match role names with loginTypes to get their IDs
      userTypesArray = loginTypes
        .filter(type => roleNames.includes(type.LoginType))
        .map(type => parseInt(type.Id));
    } else if (user.UserTypeId) {
      // Fallback to single UserTypeId if UserRoles not available
      userTypesArray = [parseInt(user.UserTypeId)];
    }
    
    setFormData({
      id: user.OrgId,
      name: user.Name,
      mobile: user.Mobile,
      email: user.Email,
      username: user.LoginName,
      password: "", // Don't show existing password
      userTypes: userTypesArray,
      isMaskingEnabled: user.cabmasking === 'Yes',
      labTypes: labTypesArray,
      status: user.Current_Status || 'Active',
      capacity: user.Capacity || "",
      isQma: user.IS_QMA || 0,
      isAcm: user.IS_ACM || 0,
      isaplicable: user.IsaplicableAss || 0,
    });
    
    // Set conditional field visibility based on user types
    if (userTypesArray.length > 0) {
      const hasAssessor = userTypesArray.includes(6);
      const hasDO = userTypesArray.includes(4);
      const requiresLab = userTypesArray.some(typeId => [4, 6, 8, 9].includes(typeId));
      
      setShowAssessorFields(hasAssessor);
      setShowCabMasking(hasDO);
      setRequiresLabType(requiresLab);
    }
    
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/super-admin-master/users/${deleteId}`,
      );
      setSuccessMessage("User deleted successfully");
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Error deleting user");
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(""), 2000);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleReset = () => {
    setFormData({
      id: null,
      name: "",
      mobile: "",
      email: "",
      username: "",
      password: "",
      userTypes: [],
      isMaskingEnabled: false,
      labTypes: [],
      status: 'Active',
      capacity: "",
      isQma: 0,
      isAcm: 0,
      isaplicable: 0,
    });
    setError("");
    setShowAssessorFields(false);
    setShowCabMasking(false);
    setRequiresLabType(false);
  };

  // MODE 12 - Export Users (with filters)
  const handleExportUsers = async () => {
    setExporting(true);
    try {
      // Build SQL condition based on current search and filter (same as fetchUsers)
      let condition = "";
      
      if (keywordSearch) {
        condition += ` AND (ut.Name LIKE '%${keywordSearch}%' OR ut.Email LIKE '%${keywordSearch}%' OR l.LoginName LIKE '%${keywordSearch}%' OR ut.Mobile LIKE '%${keywordSearch}%')`;
      }
      
      if (filterUserType) {
        const selectedType = loginTypes.find(t => t.LoginType === filterUserType);
        if (selectedType) {
          condition += ` AND l.UserTypeId = ${selectedType.Id}`;
        }
      }
      
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/super-admin-master/export-users`,
        { cond: condition }
      );
      
      if (res.data.status === "success") {
        const data = res.data.data;
        if (data && data.length > 0) {
          // Regenerate S.No as sequential numbers (1, 2, 3...)
          const exportData = data.map((row, index) => ({
            "S.No": index + 1,
            "Name": row["Name"] || row.Name,
            "Mobile Number": row["Mobile Number"] || row["MobileNumber"],
            "Email ID": row["Email ID"] || row["EmailID"],
            "Current Status": row["Current_Status"] || row.Current_Status,
            "User Roles": row["User Roles"] || row.UserRoles || "-",
            "Capacity": row["Capcity"] || row.Capacity,
            "Lab Types": row["LabTypes"] || row.LabTypes,
            "User Disciplines": row["User Disciplines"] || row["UserDisciplines"] || "-",
            "Username": row["Username"] || row.LoginName,
            "Is QMS": row["IsQMS"] || row.IsQMS
          }));
          
          // Create Excel workbook and worksheet
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          
          // Set column widths for better readability
          const columnWidths = [
            { wch: 8 },  // S.No
            { wch: 25 }, // Name
            { wch: 15 }, // Mobile Number
            { wch: 30 }, // Email ID
            { wch: 15 }, // Current Status
            { wch: 40 }, // User Roles
            { wch: 25 }, // Capacity
            { wch: 35 }, // Lab Types
            { wch: 40 }, // User Disciplines
            { wch: 20 }, // Username
            { wch: 10 }  // Is QMS
          ];
          worksheet['!cols'] = columnWidths;
          
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
          
          // Generate Excel file and download
          const excelFileName = `users_export_${new Date().toISOString().split('T')[0]}.xlsx`;
          XLSX.writeFile(workbook, excelFileName);
          
          setSuccessMessage(`${exportData.length} users exported successfully!`);
          setTimeout(() => setSuccessMessage(""), 3000);
        } else {
          setError("No data to export");
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error exporting users");
    } finally {
      setExporting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Apply client-side filters on server-paginated data (no longer needed - filtering on server)
  const filteredUsers = users;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5", p: 1 }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* Gradient Header */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            <PersonAddIcon
              sx={{ fontSize: "2rem", mr: 2, verticalAlign: "middle" }}
            />
            User Management
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Create and manage system users
          </Typography>
        </Paper>
        
        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccessMessage("")}
          >
            {successMessage}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        
        {/* Form Card */}
        <Card elevation={3} sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <PersonAddIcon
                sx={{ fontSize: "2rem", color: "#1976d2", mr: 1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#1976d2" }}
              >
                {formData.id ? "Edit User" : "Create New User"}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e9ecef",
                mb: 2,
              }}
            >
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Personal Details */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: "#555" }}>
                      Personal Details
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      placeholder="Enter Name"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      placeholder="Enter Mobile Number"
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      placeholder="Enter Email"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="User Name / Assessor ID"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      placeholder="e.g., cpd7t"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleInputChange}
                      required={!formData.id}
                      variant="outlined"
                      placeholder={formData.id ? "Leave blank to keep current" : "Enter Password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={togglePasswordVisibility}>
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      helperText="Minimum 8 chars with 1 uppercase, 1 lowercase, 1 number, 1 special character"
                    />
                  </Grid>
                  
                  {/* User Types - Multi Select */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 2, fontWeight: 600, color: "#555" }}>
                      Type of User <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    
                    <Select
                      fullWidth
                      multiple
                      name="userTypes"
                      value={formData.userTypes}
                      onChange={handleUserTypeChange}
                      displayEmpty
                      required
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <em>-- Select User Types --</em>;
                        }
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((typeId) => {
                              const type = loginTypes.find(t => parseInt(t.Id) === parseInt(typeId));
                              return (
                                <Chip
                                  key={typeId}
                                  label={type?.LoginType || `Type ${typeId}`}
                                  size="small"
                                  color="primary"
                                />
                              );
                            })}
                          </Box>
                        );
                      }}
                    >
                      {loginTypes.map((type) => (
                        <MenuItem key={type.Id} value={type.Id}>
                          <Checkbox checked={formData.userTypes.some(id => parseInt(id) === parseInt(type.Id))} />
                          {type.LoginType}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  
                  {/* Assessor-specific fields */}
                  {showAssessorFields && (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          select
                          label="Capacity"
                          name="capacity"
                          value={formData.capacity}
                          onChange={handleInputChange}
                          required={showAssessorFields}
                        >
                          <MenuItem value="">-- Select Capacity --</MenuItem>
                          <MenuItem value="LA">Lead Assessor</MenuItem>
                          <MenuItem value="TA">Tech Assessor</MenuItem>
                          <MenuItem value="LA_TA">Lead Assessor + Tech Assessor</MenuItem>
                          <MenuItem value="TE">Technical Expert</MenuItem>
                          <MenuItem value="OBS">Observer</MenuItem>
                        </TextField>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          select
                          label="Is QMA Applicable?"
                          name="isQma"
                          value={formData.isQma}
                          onChange={handleInputChange}
                        >
                          <MenuItem value={0}>N/A</MenuItem>
                          <MenuItem value={1}>Yes</MenuItem>
                        </TextField>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          select
                          label="Is ACM?"
                          name="isAcm"
                          value={formData.isAcm}
                          onChange={handleInputChange}
                        >
                          <MenuItem value={0}>No</MenuItem>
                          <MenuItem value={1}>Yes</MenuItem>
                        </TextField>
                      </Grid>
                    </>
                  )}
                  
                  {/* Lab Type Selection - Show only if required */}
                  {requiresLabType && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: "#555" }}>
                        Lab Types <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <FormGroup row>
                        {labTypes.map((lab) => (
                          <FormControlLabel
                            key={lab.id}
                            control={
                              <Checkbox
                                checked={formData.labTypes.includes(lab.id)}
                                onChange={() => handleLabTypeChange(lab.id)}
                                name={`lab-${lab.id}`}
                              />
                            }
                            label={lab.name}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                  )}
                  
                  {/* Cab Masking - Show only for DO */}
                  {showCabMasking && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isMaskingEnabled}
                            onChange={(e) => setFormData({...formData, isMaskingEnabled: e.target.checked})}
                            name="isMaskingEnabled"
                          />
                        }
                        label="Is Masking for Cab Details"
                      />
                    </Grid>
                  )}
                  
                  {/* Status */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="InActive">Inactive</MenuItem>
                      <MenuItem value="Suspend">Suspend</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
                
                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    gap: 3,
                    justifyContent: "center",
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                    sx={{ backgroundColor: "#1976d2" }}
                  >
                    {formData.id ? "Update" : "Create"} User
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RefreshIcon />}
                    disabled={loading}
                  >
                    Reset
                  </Button>
                </Box>
              </form>
            </Box>
          </CardContent>
        </Card>
        
        {/* Users List Card */}
        <Card elevation={3} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <PersonAddIcon
                sx={{ fontSize: "2rem", color: "#1976d2", mr: 1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#1976d2" }}
              >
                Users List
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Keyword Search"
                value={keywordSearch}
                onChange={(e) => {
                  setKeywordSearch(e.target.value);
                  setPage(0); // Reset to first page on search
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Select
                fullWidth
                displayEmpty
                value={filterUserType}
                onChange={(e) => {
                  setFilterUserType(e.target.value);
                  setPage(0);
                }}
                renderValue={(selected) => {
                  if (!selected) return <em>-- All User Types --</em>;
                  return selected;
                }}
              >
                <MenuItem value="">
                  <em>-- All User Types --</em>
                </MenuItem>
                {loginTypes.map((type) => (
                  <MenuItem key={type.Id} value={type.LoginType}>
                    {type.LoginType}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportUsers}
                disabled={exporting || loading}
                sx={{ height: '56px' }}
              >
                {exporting ? "Exporting..." : "Export"}
              </Button>
            </Grid>
          </Grid>
          
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell><strong>S.No</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Username</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Mobile</strong></TableCell>
                      <TableCell><strong>User Types</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">
                            No users found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers
                        .map((user, idx) => (
                          <TableRow key={user.OrgId || user.id}>
                            <TableCell>{(page * rowsPerPage) + idx + 1}</TableCell>
                            <TableCell>{user.Name || user.name}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {user.LoginName || user.username}
                              </Typography>
                            </TableCell>
                            <TableCell>{user.Email || user.email}</TableCell>
                            <TableCell>{user.Mobile || user.mobile}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                                {user.UserRoles && (
                                  <Chip
                                    label={user.UserRoles}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                {user.LabTypes && (
                                  <Chip
                                    label={user.LabTypes}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={user.Current_Status || "-"}
                                color={user.Current_Status === 'Active' ? "success" : (user.Current_Status === 'Suspend' ? "warning" : "error")}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEdit(user)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(user.OrgId || user.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: "1px solid #e0e0e0", mt: 2 }}
                showFirstButton
                showLastButton
              />
            </>
          )}
        </Card>
        
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this user? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={loading}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default CreateUsers;